import React, { useMemo } from "react";

import type { ServerDataSuccess } from "./route";

import {
  Carousel,
  Skeleton,
  CarouselSection,
  CarouselProvider,
  TabbedSection,
  CarouselScrollbar,
  CarouselControls,
  ContainerEscape,
  useCarouselScrollTo,
  type TabbedSectionProps,
} from "@bared/ui";

import { AnalyticsViewItemListProvider } from "~/providers/AnalyticsViewItemListProvider";

import { ProductCard } from "~/components/ProductCard";

import {
  useRecentlyViewed,
  useRecentlyViewedHandles,
} from "~/hooks/useRecentlyViewed";

export interface ProductRecommendationsProps {
  currentProductHandle: string;
  productRecommendations: ServerDataSuccess["productRecommendations"];
}

export function ProductRecommendations({
  currentProductHandle,
  productRecommendations,
}: ProductRecommendationsProps) {
  /**
   * Get recently viewed handles, excluding the current product, to determine
   * if we should show the "Recently Viewed" tab or not.
   */
  const recentlyViewedHandles = useRecentlyViewedHandles().filter(
    (handle) => handle !== currentProductHandle,
  );

  const tabs: TabbedSectionProps["tabs"] = [
    ...(0 < productRecommendations.length
      ? [{ title: "Recommended", handle: "recommended" }]
      : []),
    ...(0 < recentlyViewedHandles.length
      ? [{ title: "Recently Viewed", handle: "recently-viewed" }]
      : []),
  ];

  if (0 === tabs.length) return null;

  return (
    <div className="my-mega">
      <CarouselProvider>
        <ProductRecommendationsInner
          tabs={tabs}
          currentProductHandle={currentProductHandle}
          productRecommendations={productRecommendations}
        />
      </CarouselProvider>
    </div>
  );
}

function ProductRecommendationsInner({
  tabs,
  currentProductHandle,
  productRecommendations,
}: {
  currentProductHandle: string;
  tabs: TabbedSectionProps["tabs"];
  productRecommendations: ServerDataSuccess["productRecommendations"];
}) {
  const scrollTo = useCarouselScrollTo();

  return (
    <TabbedSection
      tabs={tabs}
      heading="More for you"
      onChange={() => scrollTo({ targetIndex: 0, behavior: "instant" })}
      renderAction={() => <ProductRecommendationsCarouselAction />}
    >
      {(tabHandle) => {
        if ("recommended" === tabHandle) {
          return (
            <ProductRecommendationsCarousel products={productRecommendations} />
          );
        }

        if ("recently-viewed" === tabHandle) {
          return (
            <RecentlyViewedCarousel
              currentProductHandle={currentProductHandle}
            />
          );
        }

        return null;
      }}
    </TabbedSection>
  );
}

function ProductRecommendationsCarousel({
  products,
}: {
  products: ServerDataSuccess["productRecommendations"];
}) {
  return (
    <AnalyticsViewItemListProvider
      itemListId="recommended-products"
      itemListName="Recommended Products"
    >
      <ContainerEscape config={{ default: "both", md: "both" }}>
        <Carousel
          hideNativeScrollbar
          ariaLabel="Product Recommendations"
          className="mt-mini space-x-maxi"
          slidesPerView={{ default: "1.5", sm: "2.5", md: "3.5", lg: "4.5" }}
        >
          {products.map((product, index) => (
            <CarouselSection index={index} key={product.handle}>
              <ProductCard
                {...product}
                index={index}
                sizes="(max-width: 768px) 100vw, 25vw"
              />
            </CarouselSection>
          ))}
        </Carousel>
      </ContainerEscape>
    </AnalyticsViewItemListProvider>
  );
}

function RecentlyViewedCarousel({
  currentProductHandle,
}: {
  currentProductHandle: string;
}) {
  const { products, isPending, handles } = useRecentlyViewed();

  const handlesWithoutCurrentProduct = handles.filter(
    (handle) => currentProductHandle !== handle,
  );

  const filteredProducts = useMemo(() => {
    return products.filter(
      (product) => currentProductHandle !== product.handle,
    );
  }, [products, currentProductHandle]);

  return (
    <AnalyticsViewItemListProvider
      itemListId="recently-viewed"
      itemListName="Recently Viewed"
    >
      <ContainerEscape config={{ default: "both", md: "both" }}>
        <Carousel
          hideNativeScrollbar
          ariaLabel="Recently viewed products"
          className="mt-mini space-x-maxi"
          slidesPerView={{ default: "1.5", sm: "2.5", md: "3.5", lg: "4.5" }}
        >
          {isPending
            ? new Array(handlesWithoutCurrentProduct.length)
                .fill(true)
                .map((_, index) => (
                  <CarouselSection index={index} key={`skeleton-${index}`}>
                    <Skeleton className="flex flex-col space-y-mini">
                      <div className="aspect-[3000/3273] bg-canvas" />
                      <div className="ml-mini">
                        <div className="mb-micro h-[20px] w-[75%] bg-canvas" />
                        <div className="h-[20px] w-[50%] bg-canvas" />
                      </div>
                    </Skeleton>
                  </CarouselSection>
                ))
            : filteredProducts.map((product, index) => (
                <CarouselSection index={index} key={product.handle}>
                  <ProductCard
                    {...product}
                    index={index}
                    sizes="(max-width: 768px) 100vw, 25vw"
                  />
                </CarouselSection>
              ))}
        </Carousel>
      </ContainerEscape>
    </AnalyticsViewItemListProvider>
  );
}

function ProductRecommendationsCarouselAction() {
  return (
    <>
      <div className="flex flex-col space-y-mini md:hidden">
        <CarouselScrollbar />
      </div>
      <div className="hidden items-center space-x-micro md:flex">
        <CarouselControls />
      </div>
    </>
  );
}
